// Programa de referidos — ADR-2026-08-27-programa-referidos.
//
// Todo el peso está en la base (migraciones `013a_programa_referidos_base.sql` a `023_…` de `atlas-backend/migrations/supabase/`; la copia vieja que había en `project/supabase/migrations/` se sacó — ADR-2026-09-15-referidos-la-migracion-vive-en-el-backend):
// acá sólo hay dos RPC y el armado del link. En particular NO hay nada que
// "registre" un referido: el `?ref=` ya viaja solo desde `signupAttribution.ts`
// hasta `profiles.ref_code`, y un trigger lo levanta de ahí. Si mañana aparece
// otra pantalla de alta, el programa la cubre sin tocar este archivo.

import { supabase } from './supabase';
import { APP_PUBLIC_URL } from '../config';

/** Un amigo invitado, tal como lo devuelve `referidos_resumen` (anonimizado). */
export interface AmigoReferido {
  /** Nombre de pila si lo cargó; si no, el mail enmascarado. Nunca el mail entero. */
  nombre: string;
  estado: 'pendiente' | 'activado';
  /** Lo que se acreditó por ESTE amigo. 0 si activó con el tope del mes lleno. */
  creditos: number;
  tope_alcanzado: boolean;
  /** Cuándo se registró. */
  fecha: string;
  /** Cuándo activó. `null` mientras siga pendiente. */
  activado: string | null;
}

export interface HitoReferidos {
  amigos: number;
  creditos: number;
  /** `pendiente_revision` = alcanzado, esperando que alguien lo libere a mano. */
  estado: 'acreditado' | 'pendiente_revision' | 'rechazado';
}

/** El próximo bono por cantidad de amigos, ya resuelto por el servidor. */
export interface ProximoHitoReferidos {
  /** En cuántos amigos activados está la marca. */
  amigos: number;
  /** Cuántos faltan para llegar. Nunca viene 0. */
  faltan: number;
  /** El bono que paga esa marca. */
  creditos: number;
}

export interface ResumenReferidos {
  codigo: string;
  activo: boolean;
  creditos_por_amigo: number;
  creditos_amigo_nuevo: number;
  /**
   * Lo que va a pagar el PRÓXIMO amigo que active. Hoy es igual a
   * `creditos_por_amigo` porque el monto es fijo, pero existe separado para el
   * día que vuelva la escalera por tramos: entonces los dos números dejan de
   * coincidir y el que hay que prometer es éste.
   */
  creditos_proximo_amigo: number;
  tope_mensual: number;
  /**
   * La escalera de montos por cantidad de amigos. Con monto fijo viene un solo
   * tramo (`{hasta: null}`) — o sea que NO describe un recorrido y no sirve
   * para dibujar la barra. La barra sale de `hitos_config` + `hitos`.
   */
  tramos: { hasta: number | null; creditos: number }[];
  /** `null` cuando el monto es fijo. Ver `tramos`: no usarlo para la barra. */
  proximo_tramo: { hasta: number | null; creditos: number } | null;
  /** El próximo bono. `null` si ya pasó todas las marcas. */
  proximo_hito: ProximoHitoReferidos | null;
  /** Los hitos que EXISTEN (los alcanzados y los que faltan salen de cruzar con `hitos`). */
  hitos_config: { amigos: number; creditos: number; revision: boolean }[];
  activados: number;
  pendientes: number;
  creditos_ganados: number;
  creditos_mes: number;
  hitos: HitoReferidos[];
  amigos: AmigoReferido[];
}

/** Lo que se sabe de un código ANTES de registrarse (lo puede pedir un anónimo). */
export interface InfoCodigoReferido {
  valido: boolean;
  /** Nombre de pila de quien invita. Puede faltar (nunca cargó su nombre). */
  nombre: string | null;
  creditos: number;
}

/**
 * El link que se comparte. Va a `/auth` porque es la pantalla que capta el
 * `?ref=` (`captureAttributionParams`) y donde el amigo se registra; el código
 * queda en localStorage y sobrevive a que el amigo dé vueltas antes de crearse
 * la cuenta.
 *
 * `mode=signup` NO es decorativo: sin él `/auth` abre en INICIAR SESIÓN (es su
 * default), y alguien que llega por una invitación cae en un formulario para
 * gente que ya tiene cuenta, con el cartel de "te invitó X" invisible —porque
 * sólo se muestra en el alta— justo en el momento en que más sirve. En mobile
 * el modo signup muestra el carrusel de bienvenida y el formulario vive un paso
 * más adelante, pero el código ya quedó en localStorage y el cartel aparece
 * igual cuando llega al alta.
 */
export function linkDeReferido(codigo: string): string {
  // `ref` va PRIMERO y `mode` después, aunque para el navegador dé igual: el
  // campo de la pantalla muestra el link y lo recorta cuando no entra. Con
  // `mode` adelante, lo que se cortaba era el código —lo único que distingue un
  // link de otro—; con este orden lo que se pierde es `&mode=signup`, que al
  // usuario no le dice nada.
  return `${APP_PUBLIC_URL}/auth?ref=${encodeURIComponent(codigo)}&mode=signup`;
}

export async function cargarResumenReferidos(): Promise<ResumenReferidos> {
  const { data, error } = await supabase.rpc('referidos_resumen');
  if (error) throw error;
  return data as ResumenReferidos;
}

/**
 * Cache por código, a nivel módulo. La pantalla de alta pregunta lo mismo desde
 * dos lados —el cartel del formulario y el badge del carrusel—; sin esto son dos
 * consultas idénticas por visita a `/auth`. Se guarda la PROMESA, no el
 * resultado, para que dos consumidores que montan a la vez compartan el viaje.
 */
const cacheInfoCodigo = new Map<string, Promise<InfoCodigoReferido | null>>();

/**
 * Datos del código para la pantalla de alta. Best-effort TOTAL: si falla o el
 * código no existe devuelve `null` y el alta sigue exactamente como hoy — un
 * cartel de bienvenida no puede impedirle a nadie registrarse.
 */
export function infoCodigoReferido(
  codigo: string,
): Promise<InfoCodigoReferido | null> {
  const clave = codigo.trim().toUpperCase();
  const cacheada = cacheInfoCodigo.get(clave);
  if (cacheada) return cacheada;
  const p = consultarCodigo(clave);
  cacheInfoCodigo.set(clave, p);
  return p;
}

async function consultarCodigo(
  codigo: string,
): Promise<InfoCodigoReferido | null> {
  try {
    const { data, error } = await supabase.rpc('referidos_info_codigo', {
      p_codigo: codigo,
    });
    if (error) return null;
    const info = data as InfoCodigoReferido | null;
    return info?.valido ? info : null;
  } catch {
    return null;
  }
}

/**
 * La invitación PROPIA, para el cartel de antes de pagar. Requiere sesión — a
 * diferencia de `referidos_info_codigo`, que mira un código suelto y la puede
 * pedir un anónimo en el alta.
 *
 * `tiene: false` no es sólo "no vino por un referido": el servidor también lo
 * devuelve en cuanto el premio YA se acreditó. Por eso el cartel desaparece
 * solo después de pagar y el front no tiene que acordarse de nada.
 */
export interface MiInvitacionReferido {
  tiene: boolean;
  /** Nombre de pila de quien invitó. `null` si nunca cargó su nombre. */
  nombre: string | null;
  creditos: number;
}

/**
 * Best-effort, igual que `infoCodigoReferido`: si falla devuelve `null` y el
 * checkout queda exactamente como estaba. Un cartel de regalo no puede impedir
 * un pago.
 */
export async function miInvitacionReferido(): Promise<MiInvitacionReferido | null> {
  try {
    const { data, error } = await supabase.rpc('referidos_mi_invitacion');
    if (error) return null;
    const info = data as MiInvitacionReferido | null;
    return info?.tiene ? info : null;
  } catch {
    return null;
  }
}

/** Resultado de compartir, para que la pantalla sepa qué avisar. */
export type ResultadoCompartir = 'compartido' | 'copiado' | 'cancelado' | 'error';

/**
 * Compartir el link. `navigator.share` primero (en mobile abre WhatsApp,
 * Instagram y el resto de una, que es donde este programa se juega), y el
 * portapapeles como alternativa en desktop o si el navegador no lo soporta.
 *
 * `AbortError` es el usuario cerrando la hoja de compartir: no es un fallo y no
 * tiene que caer al copiado, porque copiar sin avisar deja al usuario creyendo
 * que no pasó nada mientras el link ya está en su portapapeles.
 */
export async function compartirReferido(
  codigo: string,
  textoInvitacion: string,
): Promise<ResultadoCompartir> {
  const url = linkDeReferido(codigo);
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title: 'Estudio Atlas', text: textoInvitacion, url });
      return 'compartido';
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return 'cancelado';
      // Cualquier otro fallo (permisos, contexto no seguro): sigue al copiado.
    }
  }
  return (await copiarAlPortapapeles(url)) ? 'copiado' : 'error';
}

/**
 * Copiar con la API moderna y, si no está (Safari viejo, http, iframes sin
 * permiso), el truco del textarea. El código de referido no sirve de nada si el
 * usuario no lo puede sacar de la pantalla, así que vale el fallback feo.
 */
export async function copiarAlPortapapeles(texto: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch {
    /* sigue al fallback */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = texto;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
